import {
  useEffect,
  useId,
  useLayoutEffect,
  useState,
  type ReactElement,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import { getNavMenuAnchorBottomPx } from "./marketingNavAnchor";
import styles from "./MarketingNavMenu.module.css";

export interface MarketingNavMenuProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Assistive-tech label for the dialog (visually hidden). */
  dialogTitle?: string;
}

const NAV_MENU_OPEN_CLASS = "marketing-nav-menu-open";

/**
 * Compact marketing nav menu — full-screen panel that drops from beneath
 * `#app-top-nav` (fill-pure, text/default). The nav bar stays visible above
 * with the menu toggle swapped to close.
 */
export function MarketingNavMenu({
  open,
  onClose,
  children,
  dialogTitle = "Site navigation"
}: MarketingNavMenuProps): ReactElement | null {
  const titleId = useId();
  const [navBottomPx, setNavBottomPx] = useState(80);

  useLayoutEffect(() => {
    if (typeof document === "undefined") return;

    const syncNavBottom = (): void => {
      setNavBottomPx(getNavMenuAnchorBottomPx());
    };

    if (open) {
      document.body.classList.add(NAV_MENU_OPEN_CLASS);
      syncNavBottom();
      requestAnimationFrame(syncNavBottom);
      window.addEventListener("resize", syncNavBottom);

      const header = document.getElementById("app-top-nav");
      const anchorEl =
        header?.querySelector<HTMLElement>(".viewer-top-chrome-slide") ?? header;
      const ro =
        typeof ResizeObserver !== "undefined" && anchorEl != null
          ? new ResizeObserver(syncNavBottom)
          : null;
      if (anchorEl != null) ro?.observe(anchorEl);

      return () => {
        ro?.disconnect();
        document.body.classList.remove(NAV_MENU_OPEN_CLASS);
        window.removeEventListener("resize", syncNavBottom);
      };
    }

    document.body.classList.remove(NAV_MENU_OPEN_CLASS);
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open || typeof document === "undefined") return;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className={[styles.panel, open ? styles.panelOpen : ""].filter(Boolean).join(" ")}
      style={{ top: navBottomPx }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-hidden={!open}
    >
      <div className={styles.content}>
        <h2 id={titleId} className={styles.visuallyHidden}>
          {dialogTitle}
        </h2>
        {children}
      </div>
    </div>,
    document.body
  );
}
